import React, { useState, useEffect, useContext, useMemo } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import api from '../../services/api';
import { AuthContext } from '../../context/Auth/AuthContext';
import { useHistory } from 'react-router-dom';
import { setKanbanLaneOrder, getKanbanLaneOrder } from '../../services/companyKanbanService';
import {
  Typography, Button, TextField, Tooltip, IconButton,
  Select, MenuItem, FormControl, InputLabel,
} from '@material-ui/core';
import RefreshIcon from '@material-ui/icons/Refresh';
import AddIcon from '@material-ui/icons/Add';
import CloseIcon from '@material-ui/icons/Close';
import SearchIcon from '@material-ui/icons/Search';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import { Can } from '../../components/Can';
import KanbanBoard from './KanbanBoard';
import KanbanCreateLeadModal from './KanbanCreateLeadModal';
import KanbanImportLeadsModal from './KanbanImportLeadsModal';
import { format } from 'date-fns';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    background: '#ffffff',
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '20px 24px 0',
    flexShrink: 0,
  },
  title: {
    fontSize: '1.25rem',
    fontWeight: 700,
    color: '#1a1a1a',
    letterSpacing: '-0.3px',
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  filterBar: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '12px 24px 12px',
    borderBottom: '1px solid #ebebeb',
    flexShrink: 0,
    flexWrap: 'wrap',
  },
  filterLabel: {
    fontSize: '0.75rem',
    color: '#9b9b9b',
    fontWeight: 600,
    whiteSpace: 'nowrap',
  },
  filterSeparator: {
    width: 1,
    height: 20,
    background: '#e8e8e8',
    margin: '0 4px',
  },
  dateInput: {
    '& .MuiOutlinedInput-root': {
      borderRadius: 8,
      fontSize: '0.8rem',
      height: 32,
    },
    '& .MuiInputLabel-root': {
      fontSize: '0.8rem',
    },
    '& .MuiOutlinedInput-input': {
      padding: '6px 10px',
    },
    width: 148,
  },
  connectionSelect: {
    '& .MuiOutlinedInput-root': {
      borderRadius: 8,
      fontSize: '0.8rem',
      height: 32,
    },
    '& .MuiInputLabel-root': {
      fontSize: '0.8rem',
      transform: 'translate(10px, 9px) scale(1)',
      '&.MuiInputLabel-shrink': {
        transform: 'translate(10px, -6px) scale(0.75)',
      },
    },
    '& .MuiOutlinedInput-input': {
      padding: '5px 10px',
    },
    width: 180,
  },
  filterBtn: {
    fontSize: '0.75rem',
    fontWeight: 600,
    padding: '4px 14px',
    borderRadius: 7,
    textTransform: 'none',
    height: 32,
    background: '#1a1a1a',
    color: '#fff',
    '&:hover': { background: '#333' },
  },
  clearFilterBtn: {
    fontSize: '0.72rem',
    color: '#9b9b9b',
    padding: '4px 8px',
    borderRadius: 7,
    textTransform: 'none',
    height: 32,
    '&:hover': { color: '#1a1a1a', background: '#f5f5f5' },
  },
  createBtn: {
    fontSize: '0.78rem',
    fontWeight: 600,
    padding: '5px 14px',
    borderRadius: 7,
    textTransform: 'none',
    background: '#1a1a1a',
    color: '#fff',
    gap: 4,
    '&:hover': { background: '#333' },
  },
  newStageBtn: {
    fontSize: '0.78rem',
    fontWeight: 600,
    padding: '5px 14px',
    borderRadius: 7,
    textTransform: 'none',
    color: '#1a1a1a',
    border: '1px solid #e0e0e0',
    '&:hover': { background: '#f5f5f5' },
  },
  refreshBtn: {
    color: '#9b9b9b',
    padding: 6,
    '&:hover': { color: '#1a1a1a' },
  },
  activeFilterDot: {
    width: 6,
    height: 6,
    borderRadius: '50%',
    background: '#1a1a1a',
    display: 'inline-block',
    marginLeft: 4,
    verticalAlign: 'middle',
  },
  boardWrapper: {
    flex: 1,
    overflow: 'hidden',
    display: 'flex',
    padding: '16px 16px 0',
  },
  searchBox: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    border: '1px solid #e0e0e0',
    borderRadius: 8,
    padding: '4px 10px',
    height: 32,
    background: '#fafafa',
    minWidth: 200,
    transition: 'border-color 0.15s, background 0.15s',
    '&:focus-within': {
      borderColor: '#1a1a1a',
      background: '#fff',
    },
  },
  searchInput: {
    border: 'none',
    outline: 'none',
    background: 'transparent',
    fontSize: '0.8rem',
    color: '#1a1a1a',
    flex: 1,
    minWidth: 0,
    fontFamily: 'inherit',
    '&::placeholder': { color: '#b0b0b0' },
  },
}));

const Kanban = () => {
  const classes = useStyles();
  const history = useHistory();
  const { user, socket } = useContext(AuthContext);

  const [tags, setTags] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [lanes, setLanes] = useState([]);
  const [laneOrder, setLaneOrder] = useState(null);
  const [loadingLaneOrder, setLoadingLaneOrder] = useState(true);

  // Filtro de data
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [appliedStart, setAppliedStart] = useState('');
  const [appliedEnd, setAppliedEnd] = useState('');

  // Filtro de conexão
  const [connections, setConnections] = useState([]);
  const [selectedConnectionId, setSelectedConnectionId] = useState('');
  const [appliedConnectionId, setAppliedConnectionId] = useState('');

  // Filtro de atendente (somente admin). '' = todos, 'none' = sem responsável, '<id>' = usuário
  const isAdmin = user.profile === 'admin';
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [appliedUserId, setAppliedUserId] = useState('');

  // Filtro de tag (etiquetas normais, kanban=0). '' = todas, '<id>' = tag
  const [labelTags, setLabelTags] = useState([]);
  const [selectedTagId, setSelectedTagId] = useState('');
  const [appliedTagId, setAppliedTagId] = useState('');

  const hasActiveFilter = !!(appliedStart && appliedEnd) || !!appliedConnectionId || !!appliedUserId || !!appliedTagId;

  // Busca rápida client-side
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Modal criar lead
  const [createLeadOpen, setCreateLeadOpen] = useState(false);
  const [createLeadLaneId, setCreateLeadLaneId] = useState(null);

  // Modal importar leads do Meta
  const [importOpen, setImportOpen] = useState(false);

  // Debounce de 200ms na busca (evita re-render a cada tecla)
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 200);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Filtra lanes client-side sem chamar o backend
  const filteredLanes = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase().replace(/^#/, '');
    if (!q) return lanes;
    return lanes.map(lane => ({
      ...lane,
      tickets: lane.tickets.filter(ticket => {
        const name    = (ticket.contact?.name   || '').toLowerCase();
        const number  = (ticket.contact?.number || '').toLowerCase();
        const id      = (ticket.id ?? '').toString();
        const empresa = ((ticket.contact?.extraInfo || []).find(f => f.name === 'empresa')?.value || '').toLowerCase();
        const agent   = (ticket.user?.name || '').toLowerCase();
        return name.includes(q) || number.includes(q) || id.includes(q) || empresa.includes(q) || agent.includes(q);
      }),
    }));
  }, [lanes, debouncedSearch]);

  const queueIds = user.queues.map(queue => queue.UserQueue.queueId);

  // Carrega conexões disponíveis
  useEffect(() => {
    const loadConnections = async () => {
      try {
        const { data } = await api.get('/whatsapp');
        setConnections(data || []);
      } catch (err) {
        console.error('Erro ao carregar conexões:', err);
      }
    };
    loadConnections();
  }, []);

  // Carrega atendentes para o filtro (somente admin)
  useEffect(() => {
    if (!isAdmin) return;
    const loadUsers = async () => {
      try {
        const { data } = await api.get('/users/list');
        setUsers((data || []).map(u => ({ id: u.id, name: u.name })));
      } catch (err) {
        console.error('Erro ao carregar atendentes:', err);
      }
    };
    loadUsers();
  }, [isAdmin]);

  // Carrega etiquetas (tags normais, kanban=0) para o filtro
  useEffect(() => {
    const loadLabelTags = async () => {
      try {
        const { data } = await api.get('/tags/list', { params: { kanban: 0 } });
        setLabelTags((data || []).map(t => ({ id: t.id, name: t.name, color: t.color })));
      } catch (err) {
        console.error('Erro ao carregar etiquetas:', err);
      }
    };
    loadLabelTags();
  }, []);

  // Carrega ordem das lanes
  useEffect(() => {
    const loadLaneOrder = async () => {
      try {
        const savedOrder = await getKanbanLaneOrder();
        setLaneOrder(savedOrder);
      } catch (error) {
        console.error('Erro ao carregar ordem das lanes:', error);
      } finally {
        setLoadingLaneOrder(false);
      }
    };
    if (user && user.id) loadLaneOrder();
  }, [user]);

  useEffect(() => {
    fetchTags();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchTags = async () => {
    try {
      const response = await api.get('/tag/kanban/');
      const fetchedTags = response.data.lista || [];
      setTags(fetchedTags);
      fetchTickets(fetchedTags);
    } catch (error) {
      console.log(error);
    }
  };

  const fetchTickets = async (fetchedTags = tags, dateStart = appliedStart, dateEnd = appliedEnd) => {
    try {
      const params = { queueIds: JSON.stringify(queueIds) };
      if (dateStart) params.dateStart = dateStart;
      if (dateEnd) params.dateEnd = dateEnd;

      const { data } = await api.get('/ticket/kanban', { params });
      setTickets(data.tickets);
      organizeLanes(fetchedTags, data.tickets, appliedConnectionId, appliedUserId, appliedTagId);
    } catch (err) {
      console.log(err);
      setTickets([]);
    }
  };

  // Socket: atualiza em tempo real
  useEffect(() => {
    const companyId = user.companyId;
    const onAppMessage = data => {
      if (['create', 'update', 'delete'].includes(data.action)) {
        fetchTickets(tags, appliedStart, appliedEnd);
      }
    };
    socket.on(`company-${companyId}-ticket`, onAppMessage);
    socket.on(`company-${companyId}-appMessage`, onAppMessage);
    return () => {
      socket.off(`company-${companyId}-ticket`, onAppMessage);
      socket.off(`company-${companyId}-appMessage`, onAppMessage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, user.companyId, appliedStart, appliedEnd, appliedConnectionId, appliedUserId, appliedTagId, tags]);

  const updateTicket = updatedTicket => {
    setTickets(prev => prev.map(t => t.id === updatedTicket.id ? updatedTicket : t));
  };

  const removeTicket = ticketId => {
    setTickets(prev => prev.filter(t => t.id !== ticketId));
  };

  const organizeLanes = (fetchedTags = tags, fetchedTickets = tickets, connectionFilter = appliedConnectionId, userFilter = appliedUserId, tagFilter = appliedTagId) => {
    // Aplica filtro de conexão client-side
    let filteredTickets = connectionFilter
      ? fetchedTickets.filter(t => String(t.whatsappId) === String(connectionFilter))
      : fetchedTickets;

    // Aplica filtro de atendente client-side ('none' = sem responsável)
    if (userFilter === 'none') {
      filteredTickets = filteredTickets.filter(t => !t.userId);
    } else if (userFilter) {
      filteredTickets = filteredTickets.filter(t => String(t.userId) === String(userFilter));
    }

    // Aplica filtro de tag (etiqueta) client-side
    if (tagFilter) {
      filteredTickets = filteredTickets.filter(t => (t.tags || []).some(tt => String(tt.id) === String(tagFilter)));
    }

    const kanbanTagIds = new Set(fetchedTags.map(t => t.id));
    const defaultTickets = filteredTickets.filter(t => !t.tags.some(tt => kanbanTagIds.has(tt.id)));

    const lanesData = [
      {
        id: 'lane0',
        title: 'Novos Leads',
        tickets: defaultTickets,
        color: null,
      },
      ...fetchedTags.map(tag => ({
        id: tag.id.toString(),
        title: tag.name,
        tickets: filteredTickets.filter(t => t.tags.some(tt => tt.id === tag.id)),
        color: tag.color || null,
      })),
    ];

    if (laneOrder && laneOrder.length > 0) {
      const laneMap = new Map(lanesData.map(lane => [lane.id, lane]));
      const orderedLanes = [];
      laneOrder.forEach(laneId => {
        if (laneMap.has(laneId)) {
          orderedLanes.push(laneMap.get(laneId));
          laneMap.delete(laneId);
        }
      });
      laneMap.forEach(lane => orderedLanes.push(lane));
      setLanes(orderedLanes);
    } else {
      setLanes(lanesData);
    }
  };

  useEffect(() => {
    if (!loadingLaneOrder) organizeLanes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tags, tickets, laneOrder, loadingLaneOrder, appliedConnectionId, appliedUserId, appliedTagId]);

  // Filtros
  const handleApplyFilter = () => {
    setAppliedStart(startDate);
    setAppliedEnd(endDate);
    setAppliedConnectionId(selectedConnectionId);
    setAppliedUserId(selectedUserId);
    setAppliedTagId(selectedTagId);
    fetchTickets(tags, startDate, endDate);
    // Os filtros de conexão/atendente/tag são aplicados em organizeLanes
    organizeLanes(tags, tickets, selectedConnectionId, selectedUserId, selectedTagId);
  };

  const handleClearFilter = () => {
    setStartDate('');
    setEndDate('');
    setAppliedStart('');
    setAppliedEnd('');
    setSelectedConnectionId('');
    setAppliedConnectionId('');
    setSelectedUserId('');
    setAppliedUserId('');
    setSelectedTagId('');
    setAppliedTagId('');
    fetchTickets(tags, '', '');
  };

  // Cards
  const handleCardMove = async (ticketId, targetLaneId) => {
    ticketId = parseInt(ticketId, 10);
    try {
      await api.delete(`/ticket-tags/${ticketId}`);
      if (targetLaneId !== 'lane0') {
        await api.put(`/ticket-tags/${ticketId}/${targetLaneId}`);
      }
      fetchTickets();
    } catch (err) {
      console.log(err);
    }
  };

  const handleLaneReorder = async (sourceIndex, destinationIndex) => {
    if (user.profile !== 'admin') return;
    const newLanes = Array.from(lanes);
    const [reorderedLane] = newLanes.splice(sourceIndex, 1);
    newLanes.splice(destinationIndex, 0, reorderedLane);
    setLanes(newLanes);
    const newLaneOrder = newLanes.map(lane => lane.id);
    setLaneOrder(newLaneOrder);
    try {
      await setKanbanLaneOrder(newLaneOrder);
    } catch (error) {
      console.error('Erro ao salvar ordem das lanes:', error);
    }
  };

  // Criar lead via "+ Adicionar" de uma coluna específica
  const handleAddToColumn = (laneId) => {
    setCreateLeadLaneId(laneId);
    setCreateLeadOpen(true);
  };

  const selectedConnectionName = connections.find(c => String(c.id) === String(appliedConnectionId))?.name;

  return (
    <div className={classes.root}>
      {/* Header */}
      <div className={classes.header}>
        <Typography className={classes.title}>
          Pipeline
          {selectedConnectionName && (
            <span style={{ fontSize: '0.75rem', fontWeight: 400, color: '#9b9b9b', marginLeft: 10 }}>
              · {selectedConnectionName}
            </span>
          )}
        </Typography>
        <div className={classes.headerActions}>
          <Tooltip title="Atualizar">
            <IconButton size="small" className={classes.refreshBtn} onClick={() => fetchTickets()}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Button
            className={classes.newStageBtn}
            startIcon={<CloudUploadIcon style={{ fontSize: 15 }} />}
            onClick={() => setImportOpen(true)}
          >
            Importar Leads
          </Button>
          <Button
            className={classes.createBtn}
            startIcon={<AddIcon style={{ fontSize: 14 }} />}
            onClick={() => { setCreateLeadLaneId(null); setCreateLeadOpen(true); }}
          >
            Criar Lead
          </Button>
          <Can
            role={user.profile}
            perform="dashboard:view"
            yes={() => (
              <Button className={classes.newStageBtn} onClick={() => history.push('/tagsKanban')}>
                + Nova etapa
              </Button>
            )}
          />
        </div>
      </div>

      {/* Barra de filtro */}
      <div className={classes.filterBar}>
        {/* Filtro de data */}
        <Typography className={classes.filterLabel}>
          Data
          {!!(appliedStart && appliedEnd) && <span className={classes.activeFilterDot} />}
        </Typography>
        <TextField
          label="De"
          type="date"
          value={startDate}
          onChange={e => setStartDate(e.target.value)}
          variant="outlined"
          size="small"
          className={classes.dateInput}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="Até"
          type="date"
          value={endDate}
          onChange={e => setEndDate(e.target.value)}
          variant="outlined"
          size="small"
          className={classes.dateInput}
          InputLabelProps={{ shrink: true }}
        />

        <span className={classes.filterSeparator} />

        {/* Filtro de conexão */}
        <Typography className={classes.filterLabel}>
          Conexão
          {!!appliedConnectionId && <span className={classes.activeFilterDot} />}
        </Typography>
        <FormControl variant="outlined" size="small" className={classes.connectionSelect}>
          <InputLabel>Todas</InputLabel>
          <Select
            value={selectedConnectionId}
            onChange={e => setSelectedConnectionId(e.target.value)}
            label="Todas"
          >
            <MenuItem value=""><em>Todas as conexões</em></MenuItem>
            {connections.map(c => (
              <MenuItem key={c.id} value={c.id}>
                {c.name}
                {c.status !== 'CONNECTED' ? ' ·' : ''}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Filtro de tag (etiqueta) */}
        <span className={classes.filterSeparator} />
        <Typography className={classes.filterLabel}>
          Tag
          {!!appliedTagId && <span className={classes.activeFilterDot} />}
        </Typography>
        <FormControl variant="outlined" size="small" className={classes.connectionSelect}>
          <InputLabel>Todas</InputLabel>
          <Select
            value={selectedTagId}
            onChange={e => setSelectedTagId(e.target.value)}
            label="Todas"
          >
            <MenuItem value=""><em>Todas as tags</em></MenuItem>
            {labelTags.map(t => (
              <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Filtro de atendente (somente admin) */}
        {isAdmin && (
          <>
            <span className={classes.filterSeparator} />
            <Typography className={classes.filterLabel}>
              Atendente
              {!!appliedUserId && <span className={classes.activeFilterDot} />}
            </Typography>
            <FormControl variant="outlined" size="small" className={classes.connectionSelect}>
              <InputLabel>Todos</InputLabel>
              <Select
                value={selectedUserId}
                onChange={e => setSelectedUserId(e.target.value)}
                label="Todos"
              >
                <MenuItem value=""><em>Todos os atendentes</em></MenuItem>
                <MenuItem value="none"><em>Sem responsável</em></MenuItem>
                {users.map(u => (
                  <MenuItem key={u.id} value={u.id}>{u.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </>
        )}

        <Button className={classes.filterBtn} onClick={handleApplyFilter}>
          Filtrar
        </Button>
        {hasActiveFilter && (
          <Button
            className={classes.clearFilterBtn}
            onClick={handleClearFilter}
            startIcon={<CloseIcon style={{ fontSize: 12 }} />}
          >
            Limpar
          </Button>
        )}

        <span className={classes.filterSeparator} />

        {/* Busca rápida */}
        <div className={classes.searchBox}>
          <SearchIcon style={{ fontSize: 16, color: '#b0b0b0', flexShrink: 0 }} />
          <input
            type="text"
            placeholder="Buscar por nome, telefone, empresa, agente..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className={classes.searchInput}
          />
          {searchTerm && (
            <IconButton
              size="small"
              onClick={() => setSearchTerm('')}
              style={{ padding: 2, flexShrink: 0 }}
            >
              <CloseIcon style={{ fontSize: 12, color: '#b0b0b0' }} />
            </IconButton>
          )}
        </div>
      </div>

      {/* Board */}
      <div className={classes.boardWrapper}>
        <KanbanBoard
          lanes={filteredLanes}
          onCardMove={handleCardMove}
          onLaneReorder={handleLaneReorder}
          updateTicket={updateTicket}
          removeTicket={removeTicket}
          isAdmin={user.profile === 'admin'}
          onAddToColumn={handleAddToColumn}
          connections={connections}
        />
      </div>

      {/* Modal criar lead */}
      <KanbanCreateLeadModal
        open={createLeadOpen}
        onClose={() => setCreateLeadOpen(false)}
        lanes={lanes}
        defaultLaneId={createLeadLaneId}
        onCreated={() => fetchTickets()}
      />

      {/* Modal importar leads do Meta */}
      <KanbanImportLeadsModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        lanes={lanes}
        connections={connections}
        onImported={() => fetchTickets()}
      />
    </div>
  );
};

export default Kanban;
